import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ApiFactoryInstance } from '../api';
import { useUserAccounts } from './UserAccountsContext';
import { useAuth } from './AuthenticationContext';
const AccountDataContext = createContext(undefined);
export const AccountDataProvider = ({ children, initialAccountData, onAccountDataChange }) => {
    const [accountData, setAccountData] = useState(initialAccountData);
    const [loaded, setLoaded] = useState(!!initialAccountData);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(undefined);
    const { selectedAccountId } = useUserAccounts();
    const { authenticated } = useAuth();
    const loadAccountData = useCallback(async (accountId) => {
        // If we have cached data, don't reset loaded — show cached while refreshing
        if (!accountData || accountData.accountId !== accountId) {
            setLoaded(false);
        }
        setRefreshing(true);
        setError(undefined);
        try {
            const serverResponse = await ApiFactoryInstance.getAccountStatusApi()?.getAccountStatus(accountId);
            if (!serverResponse) {
                setError('Account data not found');
                return;
            }
            updateAccountData(accountId, serverResponse);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching account data');
        }
        finally {
            setLoaded(true);
            setRefreshing(false);
        }
    }, [accountData]);
    const refreshAccountData = useCallback(async () => {
        if (!selectedAccountId)
            return;
        await loadAccountData(selectedAccountId);
    }, [loadAccountData, selectedAccountId]);
    useEffect(() => {
        if (!authenticated)
            return;
        if (!selectedAccountId)
            return;
        if (selectedAccountId !== accountData?.accountId ||
            accountData.lastFetchedAt < new Date(Date.now() - 1000 * 60)) {
            refreshAccountData();
        }
    }, [authenticated, selectedAccountId, accountData, refreshAccountData]);
    const updateAccountData = async (accountId, data) => {
        const newAccountData = {
            balance: data.balance,
            equity: data.equity,
            openPositions: data.openPositions,
            freeMargin: data.freeMargin,
            totalProfit: data.totalProfit,
            accountId: accountId,
            pairs: data.pairs,
            lastUpdatedAt: new Date(data.lastUpdatedAt),
            lastFetchedAt: new Date()
        };
        setAccountData(newAccountData);
        onAccountDataChange?.(newAccountData);
    };
    return (_jsx(AccountDataContext.Provider, { value: {
            loaded,
            refreshing,
            accountData,
            error,
            refreshAccountData
        }, children: children }));
};
export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        throw new Error('useAccountData must be used within an AccountDataProvider');
    }
    return context;
};
